/**
 * The restricted recording screen shown to a player without the main
 * device, reached via a `#guest=<shareToken>` link (see `src/ui/gameShare.ts`
 * and `relay/gameShare.ts` for the two-token design this enforces
 * server-side). This screen can only ever submit a round proposal — it has
 * no access to editing the last round or ending the game, because the
 * `shareToken` it holds is rejected by the relay for those actions even if
 * this UI were bypassed.
 *
 * Deliberately mirrors `play.ts`'s win/draw form, minus what a guest can't
 * do. The dealer/prevailing-wind rotation is derived the same way the main
 * device derives it — `deriveTableState` replayed over the mirrored
 * `roundHistory` — so the win-context used for the pre-submission
 * validate()/score() check is real, not a placeholder. One thing this screen
 * still doesn't do that the main device does: live seat-wind /
 * prevailing-wind badges on the tile grid as you build the hand — a purely
 * visual hint, not a scoring input, so its absence here doesn't affect
 * correctness.
 */

import { DEFAULT_BRANDING, DEFAULT_WINNER_MESSAGE, renderBrandHeader } from '../branding';
import { launchConfetti } from '../confetti';
import { openDiceRoller } from '../diceRoller';
import { openHandGuide } from '../handGuide';
import { canShareAnything, prepareResultsPdf, shareButtonLabel, wireResultsShare } from '../resultsShare';
import {
  deltaSpan,
  esc,
  formatGameTimestamp,
  formatRoundListItem,
  formatWinnerBannerText,
  playerName,
  resolveWinningTile,
  vibrateSuccess,
} from '../helpers';
import { showHouseRulesPreview } from '../houseRulesPreview';
import { renderTileGrid, type TileGridContext, type TileGridHandle } from '../tileGrid';
import { mountHandPreview } from '../handPreview';
import { mountHandHelper } from '../handHelper';
import { clearDraftHand, loadDraftHand, saveDraftHand } from '../draftHand';
import { handHelperOn } from '../../game/helper';
import { circumstantialHtml, readCircumstances } from '../winForm';
import { tileGridContextFor } from '../tileGridContext';
import { renderPhotoCapture } from './photoCapture';
import { mergeScanWithEntered } from '../../vision/mergeScan';
import { getRuleset } from '../../rules';
import { describeRejection } from '../../rules/rejection';
import { computeSessionBadges, summarise } from '../../game/summary';
import { buildPlayerPosters } from '../../game/posters';
import { offerOwnPoster, posterFooter, postersCardHtml, resetPosterOffer, wirePosters } from './posters';
import { deriveTableState, seatWindOf } from '../rotation';
import type { HkosSettings } from '../../rules/hkos';
import { getOrCreatePendingSubmissionId, type SharedGameState } from '../gameShare';
import type { BonusTile, Tile, Wind } from '../../rules/tiles';
import type { HandInput, Meld, WinContext } from '../../rules/types';
import type { Game } from '../../game/types';
import { takeRecognitionFor, type RecognitionSnapshot } from '../recognitionAccuracy';
import { visibleRejection } from '../guestRejection';
import { guestLinkWindowNote } from '../photoDelivery';
import { getDefaultWinner, setDefaultWinner } from '../viewerIdentity';
import { updateMarker } from '../updateSafety';

export interface GuestWinPayload {
  winnerId: string;
  discarderId?: string;
  selfDraw: boolean;
  concealed: Tile[];
  melds: Meld[];
  winningTile: Tile;
  flowers: BonusTile[];
  claimedSet?: boolean;
  robbingKong?: boolean;
  kongReplacement?: boolean;
  lastWallTile?: boolean;
  lastDiscard?: boolean;
  heavenlyHand?: boolean;
  earthlyHand?: boolean;
  winDoubleKong?: boolean;
  photos?: string[];
  /** The photo was left out of this submission (slow connection) and will be sent separately — the host waits for it. */
  photosDeferred?: boolean;
  /** What the photo scan proposed, if this hand came from one — the host compares it with the hand it saves (see recognitionAccuracy.ts). */
  recognition?: RecognitionSnapshot;
}

export interface GuestDrawPayload {
  tenpai: string[];
}

export interface GuestPlayActions {
  /** Who this phone plays for (see `viewerIdentity.ts`), so the end of the game can offer their own poster and the winner field can default to "me". */
  identity?(): { key: string; nickname?: string };
  /**
   * `clientSubmissionId` should be the same value across every retry of one
   * logical submission attempt — the relay uses it to de-duplicate, so a
   * second tap during network lag can never create two separate pending
   * rounds. Sourced from `getOrCreatePendingSubmissionId` (see its doc
   * comment), which persists it across renders — not just a same-render
   * `crypto.randomUUID()` — since a validation failure that reaches the
   * relay, a Scan Photo round trip, Refresh, or reopening the tab all
   * re-render this screen but are still the SAME logical attempt. Retired by
   * the caller via `clearPendingSubmissionId` once a submission actually
   * succeeds, so a genuinely new round attempt after that gets a fresh one.
   */
  submitWin(payload: GuestWinPayload, clientSubmissionId: string): void;
  submitDraw(payload: GuestDrawPayload, clientSubmissionId: string): void;
  refresh(): void;
  /** Only ever wired up on the "Game Ended" screen — re-checks the game is still ended right before producing the PDF, since a download/print has no way to notice the main device went back to the game in the meantime. */
  downloadPdf(): void;
  /**
   * Silently re-fetches the mirrored game state, without re-rendering the
   * screen — unlike `refresh()`, which re-renders and would wipe out
   * whatever hand is currently tapped into the tile grid. Used right before
   * scoring a win submission, so the dealer/prevailing-wind rotation is as
   * fresh as possible even if the guest never pressed Refresh themselves.
   * Returns `undefined` on failure (e.g. offline) so the caller can fall
   * back to whatever state it already has rather than blocking submission.
   */
  fetchLatestState(): Promise<SharedGameState | undefined>;
  /**
   * A round that has been entered but not yet confirmed received by the relay (poor connection):
   * the text to show and whether it is waiting for a retry. Undefined when nothing is queued.
   */
  outboxStatus?(): { text: string; waiting: boolean } | undefined;
  /** "Try now" on the waiting banner. */
  retryOutboxNow?(): void;
  /**
   * The host held this game's photos back (slow connection): what to tell the guest and whether there is
   * anything left to fetch. Undefined when no photos were held back.
   */
  heldPhotos?(state: SharedGameState): { text: string; canGet: boolean; busy?: boolean } | undefined;
  /** "Get photos". */
  getPhotos?(): void;
  /** A note about this phone's own photo still waiting to be sent after its round. */
  ownPhotoNote?(): string | undefined;
}

/** How a render behaves beyond what it shows. */
export interface RenderOptions {
  /** False for a redraw of an already-open Final Results (confetti and scroll-to-top only belong to arriving there). Default true. */
  fresh?: boolean;
}

export type GuestMessage = { kind: 'error' | 'success'; text: string } | undefined;

/** What was already on the form before navigating to Scan Photo — restored on return, same convention as `PlayFormState`. */
export interface GuestPrefill {
  winner?: string;
  discarder?: string;
  selfDraw?: boolean;
  concealed?: Tile[];
  melds?: Meld[];
  winningTile?: Tile;
  flowers?: BonusTile[];
  claimedSet?: boolean;
  photos?: string[];
  robbingKong?: boolean;
  kongReplacement?: boolean;
  lastWallTile?: boolean;
  lastDiscard?: boolean;
  heavenlyHand?: boolean;
  earthlyHand?: boolean;
  winDoubleKong?: boolean;
  /** Tiles the most recent scan wasn't confident about — shown as a "double-check against the photo" callout, since there's no separate review screen for this anymore. */
  needsReviewTiles?: Tile[];
  /** One sentence on what was done when a scan met a hand already entered (see vision/mergeScan.ts). */
  scanNote?: string;
}

export function renderGuestPlay(
  root: HTMLElement,
  state: SharedGameState,
  message: GuestMessage,
  actions: GuestPlayActions,
  prefill?: GuestPrefill,
  options: RenderOptions = {},
): void {
  const fresh = options.fresh !== false;
  const settings = state.settings as HkosSettings;

  // The winner this phone was told is "me" (the tick under the winner field), picked again by default on the next win.
  const idKey = actions.identity?.().key;
  const remembered = idKey ? getDefaultWinner(idKey) : undefined;
  const rememberedWinner = remembered !== undefined && state.players.some((p) => p.id === remembered) ? remembered : undefined;
  const selectedWinner = prefill?.winner ?? rememberedWinner;

  const playerOptions = (selected: string | undefined) =>
    state.players
      .map((p) => `<option value="${esc(p.id)}" ${p.id === selected ? 'selected' : ''}>${esc(p.name)}</option>`)
      .join('');

  const totalsRow = state.players
    .map(
      (p) =>
        `<div class="player-tile"><div class="name">${esc(p.name)}</div><div class="score">${deltaSpan(state.totals[p.id] ?? 0)}</div></div>`,
    )
    .join('');

  // The host took the game back (or it has not ended): the next time it ends, the poster offer is made again, like the confetti.
  if (!state.ended) resetPosterOffer(actions.identity?.().key);

  if (state.ended) {
    // Same "Final Results" screen the main device shows — built from
    // `summarise()` over the mirrored round history, not a re-derived
    // approximation, so the ranking (and its tiebreak chain) can never
    // disagree with what the main device itself computed.
    const game = buildFakeGame(state);
    const summary = summarise(game);
    const posters = buildPlayerPosters(game, summary);
    const winnerNames = summary.winners.map((id) => playerName(game, id)).join(', ');

    const rankRows = summary.players
      .map(
        (p) => `
        <tr class="${p.rank === 1 ? 'winner' : ''}">
          <td>${p.rank}</td>
          <td>${esc(p.name)}</td>
          <td>${deltaSpan(p.totalScore)}</td>
          <td>${p.roundsWon}</td>
          <td>${p.selfDraws}</td>
          <td>${p.timesDiscardedInto}</td>
        </tr>
      `,
      )
      .join('');

    const roundRows = summary.rounds.map((r) => formatRoundListItem(game, r)).join('');

    // Same banner as the main device's Final Results screen (src/ui/views/
    // summary.ts) — see its guard comment for why a zero-round game is
    // skipped (every player would tie at 0 with no real winner to name).
    const winnerBanner =
      summary.roundsPlayed > 0 && summary.winners.length > 0
        ? (() => {
            const text = formatWinnerBannerText(state.branding?.winnerMessage ?? DEFAULT_WINNER_MESSAGE, winnerNames);
            return `
        <div class="winner-banner">
          <div class="winner-banner-track">
            <span>${text}</span>
            <span>${text}</span>
          </div>
        </div>`;
          })()
        : '';

    const badges = computeSessionBadges(game, summary);
    const badgesSection =
      badges.length > 0
        ? `
        <div class="session-badges">
          ${badges
            .map(
              (b) => `
              <div class="badge-chip">
                <div class="badge-label">${esc(b.label)}</div>
                <div class="badge-detail">${esc(b.detail)}</div>
              </div>`,
            )
            .join('')}
        </div>`
        : '';

    root.innerHTML = `
      ${renderBrandHeader(state.branding)}
      <p class="muted-on-page" style="margin:0 0 0.5rem">
        ${state.gameStartedAt ? `Game started: ${formatGameTimestamp(state.gameStartedAt)}<br />` : ''}
        Game ended: ${state.gameEndedAt ? formatGameTimestamp(state.gameEndedAt) : '—'}
      </p>
      ${winnerBanner}
      <div class="card">
        <h1>Final Results</h1>
        <p class="muted">Table ${esc(state.tableId)} · ${summary.roundsPlayed} round${summary.roundsPlayed === 1 ? '' : 's'} played</p>
        <p>${summary.winners.length > 1 ? 'Tied' : 'Winner'}: <strong>${esc(winnerNames)}</strong></p>

        <table class="summary">
          <thead>
            <tr><th>#</th><th>Player</th><th>Score</th><th>Rounds won</th><th>Self-draws</th><th>Fed a win</th></tr>
          </thead>
          <tbody>${rankRows}</tbody>
        </table>
        <p class="muted summary-scroll-hint">Swipe the table sideways to see every column.</p>
      <p class="muted" style="margin-top:0.5rem">Ties on total score are broken by rounds won, then by whoever reached that score first.</p>

        ${badgesSection}

        <p class="muted" style="margin-top:0.75rem">Recording a round is no longer available on this link. ${esc(guestLinkWindowNote(state))} Download your PDF before then.</p>
        <div id="guestHeldPhotos" style="display:none"></div>
        <div class="row" style="margin-top:0.5rem">
          <button type="button" id="guestPdfBtn" class="secondary">Download as PDF</button>
          ${
            canShareAnything()
              ? `<button type="button" id="guestShareBtn" class="secondary">${shareButtonLabel()}</button>`
              : ''
          }
          <button type="button" id="guestRefreshBtn" class="secondary">Refresh</button>
        </div>
      </div>

      ${postersCardHtml(posters)}

      <div class="card">
        <h2>Round by round</h2>
        <ul class="round-list" id="guestRoundList">${roundRows || '<li class="muted">No rounds played.</li>'}</ul>
      </div>
    `;
    // Same reason as views/summary.ts: this replaces the record form the
    // guest was scrolled down in.
    wirePosters(root, posters, state.branding ?? DEFAULT_BRANDING, posterFooter(state.tableId, state.gameEndedAt));
    // This phone signed in to the table as one player: offer that player their own poster (once).
    const me = actions.identity?.();
    if (me) offerOwnPoster(posters, me, state.branding ?? DEFAULT_BRANDING, posterFooter(state.tableId, state.gameEndedAt));
    if (fresh) window.scrollTo(0, 0);
    if (winnerBanner && fresh) launchConfetti();
    setGuestHeldPhotos(root, actions.heldPhotos?.(state), actions.getPhotos);

    root.querySelector<HTMLButtonElement>('#guestRefreshBtn')!.addEventListener('click', () => actions.refresh());
    root.querySelector<HTMLButtonElement>('#guestPdfBtn')!.addEventListener('click', () => actions.downloadPdf());
    const guestShareBtn = root.querySelector<HTMLButtonElement>('#guestShareBtn');
    if (guestShareBtn) {
      wireResultsShare(guestShareBtn, game, summary, prepareResultsPdf(game, summary, state.branding ?? DEFAULT_BRANDING));
    }
    return;
  }

  if (state.seatingPending) {
    // The host started this game with "Decide with the dice" and hasn't said
    // who sits where yet: the dealer and every seat wind are unknown, so a
    // round can't be recorded — or scored — until the host sets the seating.
    // Roll Dice is the point of waiting here, so it stays available.
    root.innerHTML = `
      ${renderBrandHeader(state.branding)}
      <div class="meta-bar">
        <span>Table ${esc(state.tableId)} <button type="button" id="guestModeBadge" class="tag">${settings.mode === 'fun' ? 'Fun Mode' : 'Serious Mode'}</button></span>
        <span>Seating not set yet</span>
      </div>

      <div class="row ghost-row" style="margin-bottom:0.75rem">
        <button type="button" id="rollDiceMenuBtn" class="ghost">🎲 Roll Dice</button>
        <button type="button" id="handGuideBtn" class="ghost">📖 Winning Hands</button>
      </div>

      <div class="scoreboard" id="guestScoreboard">${totalsRow}</div>
      ${LIVE_NOTE}

      <div class="card" id="seatingPendingCard">
        <h2>Waiting for the seating</h2>
        <p class="muted">
          Roll the dice with the other players to see who sits where. The host then sets the seating —
          this screen updates on its own once that's done (or tap Refresh).
        </p>
        <button type="button" id="guestRefreshBtn" style="width:100%">Refresh</button>
      </div>
    `;
    root.querySelector<HTMLButtonElement>('#guestModeBadge')!.addEventListener('click', () => {
      showHouseRulesPreview(settings);
    });
    root.querySelector<HTMLButtonElement>('#rollDiceMenuBtn')!.addEventListener('click', () => openDiceRoller());
    root.querySelector<HTMLButtonElement>('#handGuideBtn')!.addEventListener('click', () => openHandGuide(settings));
    root.querySelector<HTMLButtonElement>('#guestRefreshBtn')!.addEventListener('click', () => actions.refresh());
    return;
  }

  const circumstantial = circumstantialHtml(settings.toggles, prefill);

  const tenpaiCheckboxes = state.players
    .map(
      (p) => `<label class="checkbox-row"><input type="checkbox" class="tenpai-player" value="${esc(p.id)}" /> ${esc(p.name)}</label>`,
    )
    .join('');

  const shownRejection = visibleRejection(state.gameId, state.lastRejection);
  const rejectionNotice = shownRejection
    ? `<div class="error-box">
         Your last submitted round could not be recorded: ${esc(shownRejection.text.replace(/\.+$/, ''))}.
         Please redo it and submit again.
       </div>`
    : '';

  root.innerHTML = `
    ${renderBrandHeader(state.branding)}
    ${updateMarker('ok')}
    <div class="meta-bar">
      <span>Table ${esc(state.tableId)} <button type="button" id="guestModeBadge" class="tag">${settings.mode === 'fun' ? 'Fun Mode' : 'Serious Mode'}</button></span>
      <span id="guestRoundNo">Round ${state.roundCount + 1}</span>
    </div>

    <p class="muted">
      You're recording rounds for this game via a shared link. You can record a round you won, but only the
      main device can fix the last round or end the game. ${esc(guestLinkWindowNote(state))}
    </p>

    <div class="row ghost-row" style="margin-bottom:0.75rem">
      <button type="button" id="rollDiceMenuBtn" class="ghost">🎲 Roll Dice</button>
      <button type="button" id="handGuideBtn" class="ghost">📖 Winning Hands</button>
    </div>

    <div id="guestOutbox"></div>
    <div id="guestOwnPhoto"></div>
    <div id="guestRejection">${rejectionNotice}</div>

    <div class="scoreboard" id="guestScoreboard">${totalsRow}</div>
    ${LIVE_NOTE}
    <div id="guestHeldPhotos" style="display:none"></div>

    <div class="card">
      <h2>Record Round</h2>
      <div class="row">
        <button type="button" id="outcomeWin">Win</button>
        <button type="button" id="outcomeDraw" class="secondary">Draw</button>
      </div>

      <div id="winFields">
        <label for="winner">Winner</label>
        <select id="winner">${playerOptions(selectedWinner)}</select>
        ${idKey ? `<label class="checkbox-row" id="rememberWinnerRow">
          <input type="checkbox" id="rememberWinner" ${rememberedWinner !== undefined && rememberedWinner === selectedWinner ? 'checked' : ''} /> This is me — remember my name for my next win
        </label>` : ''}

        <label class="checkbox-row">
          <input type="checkbox" id="selfDraw" ${prefill?.selfDraw ? 'checked' : ''} /> Self-draw
        </label>

        <div id="discarderField" style="${prefill?.selfDraw ? 'display:none' : ''}">
          <label for="discarder">Discarder</label>
          <select id="discarder">${playerOptions(prefill?.discarder)}</select>
        </div>

        <button type="button" id="scanPhotoBtn" class="secondary" style="width:100%;margin:0.5rem 0">
          Scan Photo…
        </button>
        <div id="photoAttachedNote" style="display:none"></div>
        ${prefill?.scanNote ? `<p class="muted" id="scanMergeNote" style="margin:0.5rem 0">${esc(prefill.scanNote)}</p>` : ''}
        ${
          prefill?.needsReviewTiles?.length
            ? `<div class="muted" style="margin:0.5rem 0">
                <p style="margin-bottom:0.3rem">Recognition wasn't confident about ${prefill.needsReviewTiles.length === 1 ? 'this tile' : 'these tiles'} — double-check ${prefill.needsReviewTiles.length === 1 ? 'it' : 'them'} against the photo:</p>
                <div class="hand-rack">${prefill.needsReviewTiles.map((t) => `<img class="hand-tile" src="/tile-icons/${t}.png" alt="${esc(t)}" />`).join('')}</div>
              </div>`
            : ''
        }

        <div id="tileGrid"></div>

        ${circumstantial}

        <div id="handPreview" class="hand-preview"></div>
      </div>

      <div id="drawFields" style="display:none">
        <label>Players who were tenpai (ready)</label>
        ${tenpaiCheckboxes}
      </div>

      <div id="formMessage">${message ? renderMessage(message) : ''}</div>

      <button type="button" id="submitRoundBtn" style="width:100%;margin-top:1rem">Record Round</button>
      <button type="button" id="guestRefreshBtn" class="ghost" style="width:100%;margin-top:0.5rem">Refresh</button>
    </div>

    <div id="guestHistory">${renderRoundHistoryCard(state)}</div>
  `;

  if (message?.kind === 'success') vibrateSuccess();

  wireUp(root, state, actions, prefill);
  setGuestOutboxBanner(root, actions.outboxStatus?.(), actions.retryOutboxNow);
  setGuestOwnPhotoNote(root, actions.ownPhotoNote?.());
  setGuestHeldPhotos(root, actions.heldPhotos?.(state), actions.getPhotos);
}

/**
 * The card that says the host's photos were held back because of a slow connection, with the button to get
 * them. Pass `undefined` to remove it. `busy` greys the button while a fetch is running.
 */
export function setGuestHeldPhotos(
  root: HTMLElement,
  status: { text: string; canGet: boolean; busy?: boolean } | undefined,
  onGet?: () => void,
): void {
  const box = root.querySelector<HTMLElement>('#guestHeldPhotos');
  if (!box) return;
  if (!status) {
    box.style.display = 'none';
    box.innerHTML = '';
    return;
  }
  box.style.display = '';
  box.className = 'card held-photos';
  const button = status.canGet
    ? `<button type="button" id="getPhotosBtn" class="secondary" ${status.busy ? 'disabled' : ''}>${status.busy ? 'Getting photos…' : 'Get photos'}</button>`
    : '';
  box.innerHTML = `<p class="muted" style="margin:0 0 ${button ? '0.5rem' : '0'}" role="status">${esc(status.text)}</p>${button}`;
  box.querySelector<HTMLButtonElement>('#getPhotosBtn')?.addEventListener('click', () => onGet?.());
}

/** A line about this phone's own photo still waiting to be sent after its round (or how that ended). Undefined clears it. */
export function setGuestOwnPhotoNote(root: HTMLElement, text: string | undefined): void {
  const box = root.querySelector<HTMLElement>('#guestOwnPhoto');
  if (!box) return;
  box.innerHTML = text ? `<div class="result-box" role="status"><p style="margin:0">${esc(text)}</p></div>` : '';
}

/**
 * Redraws the round list after photos were added, without touching the rest of the screen (a half-entered
 * hand is safe). Returns false on the Final Results screen, which the caller redraws instead (`fresh: false`).
 */
export function refreshGuestRounds(root: HTMLElement, state: SharedGameState): boolean {
  const history = root.querySelector<HTMLElement>('#guestHistory');
  if (!history) return false;
  history.innerHTML = renderRoundHistoryCard(state);
  return true;
}

/** Says the screen keeps itself current; `setGuestLiveNote` rewrites it after each check. */
const LIVE_NOTE = `<p class="muted live-note" id="guestLiveNote" role="status">Updates on its own</p>`;

/**
 * Shows when the screen last checked for news, so a guest can trust the numbers are current —
 * or, with `problem`, why they might not be (no connection).
 */
export function setGuestLiveNote(root: HTMLElement, at: Date | undefined, problem?: string): void {
  const note = root.querySelector<HTMLElement>('#guestLiveNote');
  if (!note) return;
  note.classList.toggle('live-note-problem', !!problem);
  note.textContent =
    problem ??
    (at ? `Live · checked ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Updates on its own');
}

/**
 * The banner for a round that has not reached the host yet, and the hold on the Record Round
 * button while it is waiting (a second round could otherwise overtake the first). Pass
 * `undefined` to clear it. `waiting` means the last send failed and a retry is pending.
 */
export function setGuestOutboxBanner(
  root: HTMLElement,
  status: { text: string; waiting: boolean } | undefined,
  tryNow?: () => void,
): void {
  const box = root.querySelector<HTMLElement>('#guestOutbox');
  if (!box) return;
  if (!status) {
    box.innerHTML = '';
    return;
  }
  // No newlines inside the box: `.error-box` preserves them, which would show as blank lines.
  const tryNowBtn = status.waiting
    ? '<button type="button" id="outboxTryNowBtn" class="secondary" style="margin-top:0.5rem">Try now</button>'
    : '';
  box.innerHTML = `<div class="${status.waiting ? 'error-box' : 'result-box'}" role="status"><p style="margin:0">${esc(status.text)}</p>${tryNowBtn}</div>`;
  const submit = root.querySelector<HTMLButtonElement>('#submitRoundBtn');
  if (submit) {
    submit.disabled = true;
    submit.textContent = status.waiting ? 'Waiting to send…' : 'Sending…';
  }
  box.querySelector<HTMLButtonElement>('#outboxTryNowBtn')?.addEventListener('click', () => tryNow?.());
}

/** Set by the form each time it is drawn; lets an in-place update re-work the tile grid's wind flags. */
let refreshGridFlags: ((next: SharedGameState) => void) | undefined;

/** True while this guest is looking at a screen that `updateGuestLive` can keep current. */
export function isGuestLiveScreen(root: HTMLElement): boolean {
  return !!root.querySelector('#guestLiveNote');
}

/**
 * Brings the on-screen game up to date WITHOUT re-rendering the form, so a hand
 * the guest is in the middle of entering is never wiped by an automatic update.
 * Only the parts that come from the host change: the scoreboard, the round
 * number, the "your round could not be recorded" notice and the round list.
 *
 * Returns false when the change is bigger than that (the game ended, or the
 * host just set the seating so the form must appear): the caller then renders
 * the whole screen. Neither case has a half-entered hand to lose.
 */
export function updateGuestLive(root: HTMLElement, next: SharedGameState): boolean {
  const scoreboard = root.querySelector<HTMLElement>('#guestScoreboard');
  if (!scoreboard || next.ended) return false;

  const onForm = !!root.querySelector('#submitRoundBtn');
  const onWaiting = !!root.querySelector('#seatingPendingCard');
  if (onWaiting && !next.seatingPending) return false; // the form has to appear
  if (onForm && next.seatingPending) return false;
  if (!onForm && !onWaiting) return false;

  scoreboard.innerHTML = next.players
    .map(
      (p) =>
        `<div class="player-tile"><div class="name">${esc(p.name)}</div><div class="score">${deltaSpan(next.totals[p.id] ?? 0)}</div></div>`,
    )
    .join('');
  if (!onForm) return true;

  const roundNo = root.querySelector<HTMLElement>('#guestRoundNo');
  if (roundNo) roundNo.textContent = `Round ${next.roundCount + 1}`;
  const rejection = root.querySelector<HTMLElement>('#guestRejection');
  if (rejection) {
    const shown = visibleRejection(next.gameId, next.lastRejection);
    rejection.innerHTML = shown
      ? `<div class="error-box">
           Your last submitted round could not be recorded: ${esc(shown.text.replace(/\.+$/, ''))}.
           Please redo it and submit again.
         </div>`
      : '';
  }
  const history = root.querySelector<HTMLElement>('#guestHistory');
  if (history) history.innerHTML = renderRoundHistoryCard(next);
  // A new round can move the dealer or the round wind, which changes which tiles get the flags.
  refreshGridFlags?.(next);
  return true;
}

function renderMessage(message: NonNullable<GuestMessage>): string {
  if (message.kind === 'error') return `<div class="error-box">${esc(message.text)}</div>`;
  return `<div class="result-box"><span class="result-box-label">Submitted</span>${esc(message.text)}</div>`;
}

const SEAT_ORDER: Wind[] = ['east', 'south', 'west', 'north'];

/**
 * A `Game`-shaped view over the mirrored state, built fresh wherever needed
 * (cheap, pure) — lets this screen reuse the exact same rendering functions
 * the main device uses (`formatRoundListItem`, `summarise`, `playerName`)
 * instead of a hand-approximated copy that could drift from what those
 * actually show. `seat` is a placeholder: nothing this screen renders reads
 * it (dealer/prevailing-wind per round already live on each `RoundRecord`).
 */
export function buildFakeGame(state: SharedGameState): Game {
  return {
    id: state.gameId,
    tableId: state.tableId,
    rulesetId: state.rulesetId,
    settings: state.settings,
    players: state.players.map((p, i) => ({ id: p.id, name: p.name, seat: SEAT_ORDER[i % 4]! })),
    rounds: state.roundHistory ?? [],
    startedAt: state.gameStartedAt ?? state.createdAt,
    endedAt: state.gameEndedAt,
  };
}

function renderRoundHistoryCard(state: SharedGameState): string {
  const game = buildFakeGame(state);
  const items = [...game.rounds].reverse().map((r) => formatRoundListItem(game, r)).join('');
  return `
    <div class="card">
      <h2>Rounds (${game.rounds.length})</h2>
      <ul class="round-list">${items || '<li class="muted">No rounds recorded yet.</li>'}</ul>
    </div>
  `;
}

function wireUp(
  root: HTMLElement,
  state: SharedGameState,
  actions: GuestPlayActions,
  prefill: GuestPrefill | undefined,
): void {
  root.querySelector<HTMLButtonElement>('#guestModeBadge')!.addEventListener('click', () => {
    showHouseRulesPreview(state.settings as HkosSettings);
  });
  root.querySelector<HTMLButtonElement>('#rollDiceMenuBtn')!.addEventListener('click', () => {
    openDiceRoller();
  });
  root.querySelector<HTMLButtonElement>('#handGuideBtn')!.addEventListener('click', () => {
    openHandGuide(state.settings as HkosSettings);
  });

  // Persisted per game, not just per render — see `getOrCreatePendingSubmissionId`'s
  // doc comment and `GuestPlayActions.submitWin`'s for why a same-render-only
  // id wasn't enough to catch the retries this exists for.
  const clientSubmissionId = getOrCreatePendingSubmissionId(state.gameId);

  const winBtn = root.querySelector<HTMLButtonElement>('#outcomeWin')!;
  const drawBtn = root.querySelector<HTMLButtonElement>('#outcomeDraw')!;
  const winFields = root.querySelector<HTMLDivElement>('#winFields')!;
  const drawFields = root.querySelector<HTMLDivElement>('#drawFields')!;
  let outcome: 'win' | 'draw' = 'win';

  let preview: { refresh(): void } | undefined; // the live preview under the tiles; set once the tile grid exists
  let helper: { refresh(): void } | undefined; // the Hand helper's Suggestions under "Your hand"; likewise
  const draftKey = actions.identity?.().key; // this phone's own entry, remembered across a reload (see draftHand.ts)
  const draft = !prefill && draftKey ? loadDraftHand(draftKey) : undefined;
  const setOutcome = (o: 'win' | 'draw') => {
    outcome = o;
    preview?.refresh();
    winBtn.className = o === 'win' ? '' : 'secondary';
    drawBtn.className = o === 'draw' ? '' : 'secondary';
    winFields.style.display = o === 'win' ? '' : 'none';
    drawFields.style.display = o === 'draw' ? '' : 'none';
  };
  winBtn.addEventListener('click', () => setOutcome('win'));
  drawBtn.addEventListener('click', () => setOutcome('draw'));

  const selfDrawCb = root.querySelector<HTMLInputElement>('#selfDraw')!;
  const discarderField = root.querySelector<HTMLDivElement>('#discarderField')!;
  selfDrawCb.addEventListener('change', () => {
    discarderField.style.display = selfDrawCb.checked ? 'none' : '';
  });

  // The seat-wind / round-wind / flower flags on the tile grid, worked out exactly as the host's
  // screen does: the dealer and the round wind come from replaying the mirrored round history, and
  // the seat wind is the chosen winner's place relative to the dealer. `latestState` moves on when
  // the host publishes another round (see `updateGuestLive`), so the flags stay right without a reload.
  let latestState = state;
  const gridContext = (): TileGridContext => {
    const table = deriveTableState(buildFakeGame(latestState));
    return tileGridContextFor(
      latestState.settings as HkosSettings,
      latestState.players.map((p) => p.id),
      table.dealerIndex,
      table.prevailingWind,
      root.querySelector<HTMLSelectElement>('#winner')?.value,
    );
  };

  const tileGrid: TileGridHandle = renderTileGrid(
    root.querySelector<HTMLDivElement>('#tileGrid')!,
    {
      concealed: prefill?.concealed ?? draft?.concealed ?? [],
      melds: prefill?.melds ?? draft?.melds ?? [],
      winningTile: prefill?.winningTile ?? draft?.winningTile,
      flowers: prefill?.flowers ?? draft?.flowers ?? [],
      claimedSet: prefill?.claimedSet ?? draft?.claimedSet,
    },
    gridContext(),
    {
      onChange: () => {
        preview?.refresh();
        helper?.refresh();
        if (draftKey) saveDraftHand(draftKey, tileGrid.read());
      },
    },
  );
  root.querySelector<HTMLSelectElement>('#winner')!.addEventListener('change', () => {
    tileGrid.updateContext(gridContext());
  });

  // The same live preview as the host's form, scored with this game's rules and the rotation replayed from the mirrored history.
  preview = mountHandPreview(root.querySelector<HTMLDivElement>('#handPreview')!, buildFakeGame(state), {
    read: () => {
      if (outcome !== 'win') return undefined;
      const winnerId = root.querySelector<HTMLSelectElement>('#winner')!.value;
      const selfDraw = selfDrawCb.checked;
      const discarderId = root.querySelector<HTMLSelectElement>('#discarder')!.value;
      if (!selfDraw && discarderId === winnerId) return { blocked: 'Choose who discarded — it can\'t be the winner — to see the preview.' };
      const { concealed, melds, winningTile: picked, flowers, claimedSet } = tileGrid.read();
      const circ = readCircumstances(root);
      const game = buildFakeGame(latestState);
      const table = deriveTableState(game);
      return {
        hand: { concealed, melds, winningTile: resolveWinningTile(picked, concealed, circ.heavenlyHand), flowers, claimedSet },
        ctx: {
          winner: winnerId,
          seatWind: seatWindOf(Math.max(0, latestState.players.findIndex((p) => p.id === winnerId)), table.dealerIndex),
          prevailingWind: table.prevailingWind,
          dealer: game.players[table.dealerIndex]!.id,
          selfDraw,
          ...(selfDraw ? {} : { discarder: discarderId }),
          ...circ,
        },
      };
    },
  });
  winFields.addEventListener('change', () => preview?.refresh());

  // Suggestions (the Hand helper): follows the game's House Rules switch, which a guest's phone reads from the shared settings
  // (a leaderboard game's settings carry it as off).
  const helperSlot = root.querySelector<HTMLDivElement>('#tgHelper');
  if (helperSlot && handHelperOn({ settings: state.settings })) {
    helper = mountHandHelper(helperSlot, buildFakeGame(state), {
      read: () => {
        const { concealed, melds } = tileGrid.read();
        return { concealed, kongs: melds.filter((m) => m.type === 'kong').map((m) => ({ tile: m.tiles[0]!, concealed: m.concealed })) };
      },
    });
  }

  // "This is me": ticking it makes the chosen winner this phone's default, so the next win needs no picking; choosing a different name
  // un-ticks it (a phone that records someone else's win must not change whose phone it is), and unticking forgets the default.
  const winnerSelect = root.querySelector<HTMLSelectElement>('#winner');
  const rememberBox = root.querySelector<HTMLInputElement>('#rememberWinner');
  const key = actions.identity?.().key;
  if (winnerSelect && rememberBox && key) {
    winnerSelect.addEventListener('change', () => {
      rememberBox.checked = getDefaultWinner(key) === winnerSelect.value;
    });
    rememberBox.addEventListener('change', () => setDefaultWinner(key, rememberBox.checked ? winnerSelect.value : undefined));
  }
  refreshGridFlags = (next) => {
    latestState = next;
    tileGrid.updateContext(gridContext());
  };

  let photos: string[] = prefill?.photos ?? [];
  const photoNote = root.querySelector<HTMLDivElement>('#photoAttachedNote')!;
  const renderPhotoNote = () => {
    if (!photos.length) {
      photoNote.style.display = 'none';
      photoNote.innerHTML = '';
      return;
    }
    photoNote.style.display = '';
    photoNote.innerHTML = `
      ${photos.map((p) => `<img class="captured-photo" src="${p}" alt="Photo used for this hand" />`).join('')}
      <p class="muted">Reference photo — <a href="#" id="guestClearPhotosLink">clear</a></p>
    `;
    photoNote.querySelector<HTMLAnchorElement>('#guestClearPhotosLink')!.addEventListener('click', (e) => {
      e.preventDefault();
      photos = [];
      renderPhotoNote();
    });
  };
  renderPhotoNote();

  const readCurrentAsPrefill = (): GuestPrefill => {
    const { concealed, melds, winningTile, flowers, claimedSet } = tileGrid.read();
    return {
      winner: root.querySelector<HTMLSelectElement>('#winner')!.value,
      discarder: selfDrawCb.checked ? undefined : root.querySelector<HTMLSelectElement>('#discarder')!.value,
      selfDraw: selfDrawCb.checked,
      concealed,
      melds,
      winningTile,
      flowers,
      claimedSet,
      photos,
      robbingKong: root.querySelector<HTMLInputElement>('#robbingKong')?.checked,
      kongReplacement: root.querySelector<HTMLInputElement>('#kongReplacement')?.checked,
      lastWallTile: root.querySelector<HTMLInputElement>('#lastWallTile')?.checked,
      lastDiscard: root.querySelector<HTMLInputElement>('#lastDiscard')?.checked,
      heavenlyHand: root.querySelector<HTMLInputElement>('#heavenlyHand')?.checked,
      earthlyHand: root.querySelector<HTMLInputElement>('#earthlyHand')?.checked,
      winDoubleKong: root.querySelector<HTMLInputElement>('#winDoubleKong')?.checked,
    };
  };

  root.querySelector<HTMLButtonElement>('#scanPhotoBtn')!.addEventListener('click', () => {
    const carryOver = readCurrentAsPrefill();
    renderPhotoCapture(
      root,
      state.tableId,
      {
        confirm: (result) => {
          // What the player had already entered is kept wherever the photo is in doubt (vision/mergeScan.ts).
          const merged = mergeScanWithEntered(
            { concealed: result.concealed, melds: result.melds, flowers: result.flowers, flaggedIndices: result.needsReviewIndices },
            { concealed: carryOver.concealed ?? [], melds: carryOver.melds ?? [], winningTile: carryOver.winningTile, flowers: carryOver.flowers ?? [], claimedSet: carryOver.claimedSet },
          );
          renderGuestPlay(root, state, undefined, actions, {
            ...carryOver, // the winner, discarder, self-draw and the ticked extras are the player's own too
            concealed: merged.concealed,
            melds: merged.melds,
            flowers: merged.flowers,
            winningTile: merged.winningTile,
            claimedSet: merged.claimedSet,
            photos: result.photos,
            needsReviewTiles: merged.stillFlagged,
            ...(merged.note ? { scanNote: merged.note } : {}),
          });
        },
        cancel: () => renderGuestPlay(root, state, undefined, actions, carryOver),
      },
      // No paid relay path is reachable without an activation session, and
      // guest devices never have one — this limit only matters if the
      // device's own recognitionEngine setting is 'relay' (see
      // photoCapture.ts), so it's generous rather than a real cost control.
      { maxAttempts: 3, attemptsUsed: 0, onAttempt: () => {} },
    );
  });

  const formMessage = root.querySelector<HTMLDivElement>('#formMessage')!;
  const showError = (text: string) => {
    formMessage.innerHTML = renderMessage({ kind: 'error', text });
  };

  root.querySelector<HTMLButtonElement>('#guestRefreshBtn')!.addEventListener('click', () => actions.refresh());

  const submitBtn = root.querySelector<HTMLButtonElement>('#submitRoundBtn')!;
  const submitBtnLabel = submitBtn.textContent ?? 'Record Round';
  // Disabled the instant it's tapped, re-enabled only on a validation error
  // the guest needs to fix — never on the way to an actual submission. This
  // is what makes a second tap during network lag impossible instead of
  // just unlikely: a guest submitted the same win twice because an unclear
  // "did that work?" moment led to a second tap, which used to create a
  // second, genuinely separate pending round on the relay (see
  // `relay/gameShare.ts`'s `submitRound` — no de-duplication otherwise).
  const setSubmitting = (submitting: boolean) => {
    submitBtn.disabled = submitting;
    submitBtn.textContent = submitting ? 'Submitting…' : submitBtnLabel;
  };

  submitBtn.addEventListener('click', () => {
    setSubmitting(true);

    if (outcome === 'draw') {
      const tenpai = [...root.querySelectorAll<HTMLInputElement>('.tenpai-player:checked')].map((el) => el.value);
      actions.submitDraw({ tenpai }, clientSubmissionId);
      return;
    }

    const winnerId = root.querySelector<HTMLSelectElement>('#winner')!.value;
    const selfDraw = selfDrawCb.checked;
    const discarderId = root.querySelector<HTMLSelectElement>('#discarder')!.value;

    if (!selfDraw && discarderId === winnerId) {
      showError('The discarder must be a different player from the winner.');
      setSubmitting(false);
      return;
    }
    if (tileGrid.hasUnsavedClaim()) {
      showError('You have an unfinished kong — tap "Add Kong" to include it, or "Cancel" to discard it.');
      setSubmitting(false);
      return;
    }

    const { concealed, melds, winningTile: pickedWinningTile, flowers, claimedSet } = tileGrid.read();
    const heavenlyHand = root.querySelector<HTMLInputElement>('#heavenlyHand')?.checked;
    const winningTile = resolveWinningTile(pickedWinningTile, concealed, heavenlyHand ?? false);
    if (!winningTile) {
      showError('Select the winning tile.');
      setSubmitting(false);
      return;
    }

    const robbingKong = root.querySelector<HTMLInputElement>('#robbingKong')?.checked;
    const kongReplacement = root.querySelector<HTMLInputElement>('#kongReplacement')?.checked;
    const lastWallTile = root.querySelector<HTMLInputElement>('#lastWallTile')?.checked;
    const lastDiscard = root.querySelector<HTMLInputElement>('#lastDiscard')?.checked;
    const earthlyHand = root.querySelector<HTMLInputElement>('#earthlyHand')?.checked;
    const winDoubleKong = root.querySelector<HTMLInputElement>('#winDoubleKong')?.checked;

    void (async () => {
      try {
        await submitAfterValidating(winningTile);
      } catch {
        // A hard network failure (offline, DNS, connection reset) throws
        // rather than resolving — without this, the button would stay stuck
        // on "Submitting…" forever with no explanation, which is the exact
        // "did that work or not?" ambiguity that caused the double-submit
        // this fix exists for in the first place. A validation-level failure
        // never reaches here — those already show their own specific message
        // and re-enable the button themselves, below.
        showError('Could not reach the game — check your connection and try again.');
        setSubmitting(false);
      }
    })();

    async function submitAfterValidating(winningTile: Tile): Promise<void> {
      // Silently re-fetch right before scoring — non-rendering (unlike the
      // Refresh button), so it can't wipe out the hand just entered — so the
      // dealer/prevailing-wind rotation below reflects any rounds recorded
      // elsewhere since this screen last loaded. Falls back to the state
      // already on screen if the relay returns an error response (e.g. the
      // link expired); that state's roundHistory is still real, just
      // possibly a little stale. A true network-level failure instead
      // throws, caught by the caller above.
      const latest = (await actions.fetchLatestState()) ?? state;
      const game = buildFakeGame(latest);
      const table = deriveTableState(game);
      const winnerIndex = latest.players.findIndex((p) => p.id === winnerId);

      // The main device catches a bad hand instantly (recordWin validates in
      // the same step it's entered), but a guest submission only gets checked
      // later, asynchronously, when the main device applies it — without
      // this, a mistake (a kong tapped 4x into the grid instead of using
      // "+ Add Kong", or "Kong replacement win" checked with no kong declared)
      // would submit "successfully" and only surface as a rejection much
      // later, or never if nobody checks. Calls the actual ruleset's own
      // validate() — not a hand-copied subset of its checks — so this can
      // never drift from what the main device enforces. seatWind/
      // prevailingWind/dealer are the real rotation — derived the same way
      // the main device derives it (`deriveTableState` replayed over the
      // mirrored round history), not a placeholder.
      const hand: HandInput = { concealed, melds, winningTile, flowers, claimedSet };
      const validationCtx: WinContext = {
        winner: winnerId,
        seatWind: seatWindOf(winnerIndex, table.dealerIndex),
        prevailingWind: table.prevailingWind,
        dealer: game.players[table.dealerIndex]!.id,
        selfDraw,
        ...(selfDraw ? {} : { discarder: discarderId }),
        robbingKong,
        kongReplacement,
        lastWallTile,
        lastDiscard,
        heavenlyHand,
        earthlyHand,
        winDoubleKong,
      };
      const ruleset = getRuleset(state.rulesetId);
      const problems = ruleset.validate(hand, validationCtx, state.settings);
      if (problems.length > 0) {
        showError(problems.join('; '));
        setSubmitting(false);
        return;
      }

      // Below-minimum-fan is the other common way an entered hand turns out
      // not to be a valid win (e.g. a "chicken hand" with no scoring pattern
      // at all) — validate() alone never catches this, since it's a scoring
      // outcome, not a shape problem. Now that seatWind/prevailingWind/dealer
      // are the real rotation rather than a placeholder, this fan count
      // (including any seat/prevailing wind-pung or flower/season bonus)
      // matches what the main device will compute, as long as this screen's
      // last-fetched round history is current — the refresh right above this
      // is what keeps it that way.
      const result = ruleset.score(hand, validationCtx, state.settings);
      if (!result.meetsMinimum) {
        showError(describeRejection(result));
        setSubmitting(false);
        return;
      }

      const scan = takeRecognitionFor(photos);
      if (draftKey) clearDraftHand(draftKey); // the hand is on its way: the next round starts empty
      actions.submitWin({
        winnerId,
        ...(selfDraw ? {} : { discarderId }),
        selfDraw,
        concealed,
        melds,
        winningTile,
        flowers,
        claimedSet,
        robbingKong,
        kongReplacement,
        lastWallTile,
        lastDiscard,
        heavenlyHand,
        earthlyHand,
        winDoubleKong,
        ...(photos.length ? { photos } : {}),
        ...(scan ? { recognition: scan } : {}),
      }, clientSubmissionId);
    }
  });
}
